import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import styles from './Profile.module.css';
import { getAuthToken } from '../lib/auth';
import { HOST, resolveMediaUrl } from '../config/api';
import { DEFAULT_AI_MODEL, getAiModelLabel, resolveAiModelOptions } from '../features/coach/aiModels';
import AiUsageTable from '../features/coach/AiUsageTable';
import SubscriptionTable from './user/SubscriptionTable';
import Toast from './toast/Toast';

const PROFILE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const PROFILE_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

const Profile = ({ userId, setUserInfo, userInfo, userRole, demoMode = false }) => {
    const MAX_PROFILE_IMAGE_SIZE_MB = 5;
    const MAX_PROFILE_IMAGE_SIZE_BYTES = MAX_PROFILE_IMAGE_SIZE_MB * 1024 * 1024;
    const [showShareToast, setShowShareToast] = useState(false);
    const [coachBio, setCoachBio] = useState(userInfo?.bio || '');
    const [coachImagePreview, setCoachImagePreview] = useState(resolveMediaUrl(userInfo?.image || userInfo?.imageUrl));
    const [coachImageFile, setCoachImageFile] = useState(null);
    const [coachProfileSaved, setCoachProfileSaved] = useState(false);
    const [coachProfileSaving, setCoachProfileSaving] = useState(false);
    const [coachImageError, setCoachImageError] = useState('');
    const [coachProfileError, setCoachProfileError] = useState('');
    const [isAiPromptOpen, setIsAiPromptOpen] = useState(false);
    const [coachAiPrompt, setCoachAiPrompt] = useState(userInfo?.aiPrompt || '');
    const [coachAiPromptSaved, setCoachAiPromptSaved] = useState(false);
    const [coachAiPromptSaving, setCoachAiPromptSaving] = useState(false);
    const [coachAiPromptError, setCoachAiPromptError] = useState('');
    const [coachAiParameters, setCoachAiParameters] = useState(userInfo?.aiParameters || '');
    const [coachAiModel, setCoachAiModel] = useState(userInfo?.aiModel || DEFAULT_AI_MODEL);
    const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(Boolean(userInfo?.emailNotificationsEnabled));
    const [athletePreferencesSaved, setAthletePreferencesSaved] = useState(false);
    const [athletePreferencesSaving, setAthletePreferencesSaving] = useState(false);
    const [athletePreferencesError, setAthletePreferencesError] = useState('');
    const coachImagePreviewObjectUrlRef = useRef(null);

    const location = useLocation();
    const navigate = useNavigate();
    const fromShare = location.state?.fromShare === true;
    const experimentalAiEnabled = demoMode
        ? userInfo?.experimentalAiEnabled ?? true
        : Boolean(userInfo?.experimentalAiEnabled);

    useEffect(() => {
        if (fromShare) {
            setShowShareToast(true);
            navigate('.', { replace: true, state: {} });
        }
    }, [fromShare, navigate]);

    useEffect(() => {
        if (coachImagePreviewObjectUrlRef.current) {
            URL.revokeObjectURL(coachImagePreviewObjectUrlRef.current);
            coachImagePreviewObjectUrlRef.current = null;
        }
        setCoachBio(userInfo?.bio || '');
        setCoachImagePreview(resolveMediaUrl(userInfo?.image || userInfo?.imageUrl));
        setCoachImageFile(null);
        setCoachImageError('');
        setCoachProfileError('');
        setCoachAiPrompt(userInfo?.aiPrompt || '');
        setCoachAiParameters(userInfo?.aiParameters || '');
        setCoachAiModel(userInfo?.aiModel || DEFAULT_AI_MODEL);
        setCoachAiPromptSaved(false);
        setCoachAiPromptSaving(false);
        setCoachAiPromptError('');
        setEmailNotificationsEnabled(Boolean(userInfo?.emailNotificationsEnabled));
        setAthletePreferencesSaved(false);
        setAthletePreferencesSaving(false);
        setAthletePreferencesError('');
    }, [userInfo]);

    useEffect(() => () => {
        if (coachImagePreviewObjectUrlRef.current) {
            URL.revokeObjectURL(coachImagePreviewObjectUrlRef.current);
        }
    }, []);

    const userActivatedVariant = async (variantId) => {
        if (demoMode) {
            setUserInfo((prevState) => ({
                ...prevState,
                activeVariant: variantId,
            }));
            return;
        }

        try {
            const response = await fetch(`${HOST}/v1/set_active_variant`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    userId,
                    variantId,
                }),
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok) {
                console.error('Failed to activate training plan:', response.statusText);
                return;
            }

            setUserInfo((prevState) => ({
                ...prevState,
                activeVariant: variantId,
            }));
        } catch (error) {
            console.error('Failed to activate training plan:', error);
        }
    };

    const handleCoachImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }

        if (file.size > MAX_PROFILE_IMAGE_SIZE_BYTES) {
            setCoachImageFile(null);
            setCoachProfileSaved(false);
            setCoachImageError(`Image size is too big. Maximum size is ${MAX_PROFILE_IMAGE_SIZE_MB} MB.`);
            e.target.value = '';
            return;
        }

        const fileName = file.name.toLowerCase();
        const hasAllowedExtension = PROFILE_IMAGE_EXTENSIONS.some((extension) => fileName.endsWith(extension));
        if (!PROFILE_IMAGE_TYPES.has(file.type) || !hasAllowedExtension) {
            setCoachImageFile(null);
            setCoachProfileSaved(false);
            setCoachImageError('Upload a JPG, PNG, or WEBP image.');
            e.target.value = '';
            return;
        }

        if (coachImagePreviewObjectUrlRef.current) {
            URL.revokeObjectURL(coachImagePreviewObjectUrlRef.current);
        }

        const previewUrl = URL.createObjectURL(file);
        coachImagePreviewObjectUrlRef.current = previewUrl;
        setCoachImageFile(file);
        setCoachImagePreview(previewUrl);
        setCoachProfileSaved(false);
        setCoachImageError('');
        setCoachProfileError('');
    };

    const handleCoachProfileSave = async (e) => {
        e.preventDefault();

        if (demoMode) {
            setUserInfo((prevState) => ({
                ...prevState,
                bio: coachBio,
                image: coachImagePreview,
                emailNotificationsEnabled,
            }));
            setCoachProfileSaved(true);
            setCoachProfileError('');
            return;
        }

        setCoachProfileSaving(true);
        setCoachProfileError('');

        try {
            let imageUrl = userInfo?.image || null;

            if (coachImageFile) {
                const formData = new FormData();
                formData.append('userId', userId);
                formData.append('image', coachImageFile);

                const imageResponse = await fetch(`${HOST}/v1/profile_image`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`,
                    },
                    body: formData,
                });

                if (imageResponse.status === 401) {
                    navigate('/login');
                    return;
                }

                if (!imageResponse.ok) {
                    const imageErrorData = await imageResponse.json().catch(() => null);
                    const backendMessage = imageErrorData?.message;

                    if (backendMessage === 'Image is too large') {
                        setCoachImageError(`Image size is too big. Maximum size is ${MAX_PROFILE_IMAGE_SIZE_MB} MB.`);
                    } else {
                        setCoachImageError(backendMessage || 'Profile image upload failed. Please try again.');
                    }
                    return;
                }

                const imageData = await imageResponse.json();
                imageUrl = imageData.imageUrl || imageUrl;
                setCoachImagePreview(resolveMediaUrl(imageUrl));
                setCoachImageError('');
            }

            const response = await fetch(`${HOST}/v1/profile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    userId,
                    bio: coachBio,
                    imageUrl,
                    emailNotificationsEnabled,
                }),
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok) {
                setCoachProfileError('Coach profile could not be saved. Please try again.');
                console.error('Failed to save coach profile:', response.statusText);
                return;
            }

            const data = await response.json();
            if (data?.user) {
                setUserInfo(data.user);
                setCoachImagePreview(resolveMediaUrl(data.user.image || data.user.imageUrl || imageUrl));
            }

            setCoachImageFile(null);
            setCoachProfileSaved(true);
            setCoachProfileError('');
        } catch (error) {
            setCoachProfileError('Coach profile could not be saved. Please try again.');
            console.error('Failed to save coach profile:', error);
        } finally {
            setCoachProfileSaving(false);
        }
    };

    const handleAthletePreferenceSave = async (e) => {
        e.preventDefault();

        if (demoMode) {
            setUserInfo((prevState) => ({
                ...prevState,
                emailNotificationsEnabled,
            }));
            setAthletePreferencesSaved(true);
            setAthletePreferencesError('');
            return;
        }

        setAthletePreferencesSaving(true);
        setAthletePreferencesError('');

        try {
            const response = await fetch(`${HOST}/v1/profile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    userId,
                    emailNotificationsEnabled,
                }),
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok) {
                setAthletePreferencesError('Notification preferences could not be saved. Please try again.');
                return;
            }

            const data = await response.json();
            if (data?.user) {
                setUserInfo(data.user);
            }

            setAthletePreferencesSaved(true);
            setAthletePreferencesError('');
        } catch (error) {
            setAthletePreferencesError('Notification preferences could not be saved. Please try again.');
            console.error('Failed to save athlete notification preferences:', error);
        } finally {
            setAthletePreferencesSaving(false);
        }
    };

    const handleCoachAiPromptSave = async (e) => {
        e.preventDefault();

        if (demoMode) {
            setUserInfo((prevState) => ({
                ...prevState,
                aiPrompt: coachAiPrompt,
                aiParameters: coachAiParameters,
                aiModel: coachAiModel,
                aiGenerationEnabled: coachAiPrompt.trim() !== '' && coachAiParameters.trim() !== '',
            }));
            setCoachAiPromptSaved(true);
            setCoachAiPromptError('');
            return;
        }

        setCoachAiPromptSaving(true);
        setCoachAiPromptError('');

        try {
            const response = await fetch(`${HOST}/v1/coach_prompt`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    userId,
                    prompt: coachAiPrompt,
                    parameters: coachAiParameters,
                    model: coachAiModel,
                }),
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok) {
                setCoachAiPromptError('AI training prompt could not be saved. Please try again.');
                return;
            }

            const data = await response.json();
            if (data?.user) {
                setUserInfo(data.user);
            }

            setCoachAiPromptSaved(true);
            setCoachAiPromptError('');
        } catch (error) {
            setCoachAiPromptError('AI training prompt could not be saved. Please try again.');
            console.error('Failed to save AI training prompt:', error);
        } finally {
            setCoachAiPromptSaving(false);
        }
    };

    if (!userInfo || userRole === null) {
        return <div className={styles.loadingElm}>Loading...</div>;
    }

    const activeSubscription = userInfo.coachSubscription?.find(
        (subscription) => subscription.variant_id === userInfo.activeVariant
    );

    const coachSubscriberCount = userInfo.subscriberCount || 0;
    const coachTrainingPlansCount = userInfo.trainingPlansCount || 0;
    const coachCompletedTrainingsCount = userInfo.completedTrainingsCount || 0;

    return (
        <div className={styles.profileContainer}>
            <div className={styles.profileHero}>
                <div>
                    <span className={styles.kicker}>{userRole === 1 ? 'Coach profile' : 'Your profile'}</span>
                    <h2>{`${userInfo.firstName} ${userInfo.lastName}`}</h2>
                    <p>
                        {userRole === 1
                            ? 'Keep your public coach presence up to date and review how your account is performing.'
                            : 'Keep your subscriptions clear, choose your active training plan, and track your overall training rhythm.'}
                    </p>
                </div>
            </div>

            {userRole === 2 && (
                <>
                    <section className={styles.statsGrid}>
                        <article className={styles.statCard}>
                            <span>Coach subscriptions</span>
                            <strong>{userInfo.coachSubscription?.length || 0}</strong>
                            <small>Plans available to you right now</small>
                        </article>
                        <article className={styles.statCard}>
                            <span>Completed trainings</span>
                            <strong>{userInfo.completedTrainingsCount || 0}</strong>
                            <small>Tracked sessions completed so far</small>
                        </article>
                        <article className={styles.statCard}>
                            <span>Active plan</span>
                            <strong>{activeSubscription?.name || 'None'}</strong>
                            <small>
                                {activeSubscription
                                    ? `${activeSubscription.first_name} ${activeSubscription.last_name}`
                                    : 'Set an active subscription below'}
                            </small>
                        </article>
                    </section>

                    <section className={styles.profileGrid}>
                        <article className={`${styles.section} ${styles.sectionWide}`}>
                            <div className={styles.sectionHeader}>
                                <div>
                                    <h3 className={styles.createTrainingHeader}>Your subscriptions</h3>
                                    <p className={styles.sectionDescription}>
                                        Choose which plan should be active so the dashboard and Trainings tab know which session to prioritize.
                                    </p>
                                </div>
                            </div>
                            <SubscriptionTable
                                coachSubscriptions={userInfo.coachSubscription}
                                activeVariantId={userInfo.activeVariant}
                                onActivate={userActivatedVariant}
                            />
                        </article>

                        <article className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div>
                                    <h3 className={styles.createTrainingHeader}>Account overview</h3>
                                    <p className={styles.sectionDescription}>
                                        A compact summary of who you are training with and what is currently active.
                                    </p>
                                </div>
                            </div>

                            <section className={styles.notificationPanel}>
                                <div className={styles.notificationPanelHeader}>
                                    <div>
                                        <h4>Notifications</h4>
                                        <p>Choose how you want to be notified when a coach adds a new training to any plan you are subscribed to.</p>
                                    </div>
                                </div>

                                {athletePreferencesSaved ? (
                                    <p className={styles.variantSaved}>Notification preferences saved successfully.</p>
                                ) : null}

                                <form onSubmit={handleAthletePreferenceSave} className={styles.trainingForm}>
                                    <label className={styles.preferenceRow}>
                                        <input
                                            type="checkbox"
                                            checked={emailNotificationsEnabled}
                                            onChange={(e) => {
                                                setEmailNotificationsEnabled(e.target.checked);
                                                setAthletePreferencesSaved(false);
                                            }}
                                        />
                                        <span>
                                            <strong>Email</strong>
                                            <small>Receive an email each time a coach adds a new training to a plan you are subscribed to.</small>
                                        </span>
                                    </label>

                                    {athletePreferencesError ? (
                                        <p className={styles.formError}>{athletePreferencesError}</p>
                                    ) : null}

                                    <button type="submit" className={styles.submitButton} disabled={athletePreferencesSaving}>
                                        {athletePreferencesSaving ? 'Saving...' : 'Save changes'}
                                    </button>
                                </form>
                            </section>

                            <div className={styles.profileSummary}>
                                <div className={styles.summaryItem}>
                                    <span>Name</span>
                                    <strong>{`${userInfo.firstName} ${userInfo.lastName}`}</strong>
                                </div>
                                <div className={styles.summaryItem}>
                                    <span>Current coach</span>
                                    <strong>
                                        {activeSubscription
                                            ? `${activeSubscription.first_name} ${activeSubscription.last_name}`
                                            : 'No active coach'}
                                    </strong>
                                </div>
                                <div className={styles.summaryItem}>
                                    <span>Current plan</span>
                                    <strong>{activeSubscription?.name || 'No active plan'}</strong>
                                </div>
                            </div>
                        </article>
                    </section>
                </>
            )}

            {userRole === 1 && (
                <>
                    <section className={styles.statsGrid}>
                        <article className={styles.statCard}>
                            <span>Subscribers</span>
                            <strong>{coachSubscriberCount}</strong>
                            <small>Athletes currently following your plans</small>
                        </article>
                        <article className={styles.statCard}>
                            <span>Completed trainings</span>
                            <strong>{coachCompletedTrainingsCount}</strong>
                            <small>Recorded athlete sessions across your plans</small>
                        </article>
                        <article className={styles.statCard}>
                            <span>Training plans</span>
                            <strong>{coachTrainingPlansCount}</strong>
                            <small>Plans currently in your coaching catalog</small>
                        </article>
                    </section>

                    <section className={styles.coachTopGrid}>
                        <article className={`${styles.section} ${styles.sectionWide}`}>
                            <div className={styles.sectionHeader}>
                                <div>
                                    <h3 className={styles.createTrainingHeader}>Public coach profile</h3>
                                    <p className={styles.sectionDescription}>
                                        Add a short introduction and a profile image so athletes can quickly understand who
                                        they are subscribing to.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.coachIdentityCard}>
                                <div className={styles.coachIdentityHeader}>
                                    <img
                                        src={coachImagePreview || '/no_photo.jpg'}
                                        alt={`${userInfo.firstName} ${userInfo.lastName}`}
                                        className={styles.coachAvatar}
                                    />
                                    <div className={styles.coachIdentityMeta}>
                                        <h4>{`${userInfo.firstName} ${userInfo.lastName}`}</h4>
                                        <p>This intro and placeholder photo will be shown on the Coaches page.</p>
                                    </div>
                                </div>

                                {coachProfileSaved && (
                                    <p className={styles.variantSaved}>Coach profile saved successfully.</p>
                                )}

                                <form onSubmit={handleCoachProfileSave} className={styles.trainingForm}>
                                    <div className={styles.uploadRow}>
                                        <label htmlFor="coachPhotoUpload" className={styles.uploadButton}>
                                            Upload new profile picture
                                        </label>
                                        <input
                                            id="coachPhotoUpload"
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            className={styles.hiddenFileInput}
                                            onChange={handleCoachImageChange}
                                        />
                                        <span className={styles.uploadHint}>
                                            JPG, PNG, or WEBP up to {MAX_PROFILE_IMAGE_SIZE_MB} MB. Recommended under 2 MB.
                                        </span>
                                    </div>

                                    {coachImageError ? (
                                        <p className={styles.fieldError}>{coachImageError}</p>
                                    ) : null}

                                    <div>
                                        <label htmlFor="coachBio" className={styles.componentLabel}>
                                            About you
                                        </label>
                                        <textarea
                                            id="coachBio"
                                            value={coachBio}
                                            onChange={(e) => {
                                                setCoachBio(e.target.value);
                                                setCoachProfileSaved(false);
                                            }}
                                            placeholder="Write a few sentences about your coaching style, focus, and who you help."
                                            className={styles.textArea}
                                        />
                                    </div>

                                    {experimentalAiEnabled ? (
                                        <section className={styles.experimentalPanel}>
                                            <button
                                                type="button"
                                                className={styles.experimentalToggle}
                                                onClick={() => setIsAiPromptOpen((value) => !value)}
                                                aria-expanded={isAiPromptOpen}
                                            >
                                                <div>
                                                    <div className={styles.experimentalPills}>
                                                        <span className={styles.kicker}>AI training</span>
                                                        <span className={styles.experimentalPill}>Experimental feature</span>
                                                    </div>
                                                    <h4>AI training prompt</h4>
                                                    <p className={styles.sectionDescription}>
                                                        Specify your ai prompt and the default parameters used to generate trainings here
                                                    </p>
                                                </div>
                                                <span className={`${styles.experimentalChevron} ${isAiPromptOpen ? styles.experimentalChevronOpen : ''}`}>
                                                    ▾
                                                </span>
                                            </button>

                                            {isAiPromptOpen ? (
                                                <div className={styles.trainingForm}>
                                                    <div>
                                                        <label htmlFor="coachAiModel" className={styles.componentLabel}>
                                                            AI model
                                                        </label>
                                                        <select
                                                            id="coachAiModel"
                                                            aria-label="AI model"
                                                            value={coachAiModel}
                                                            onChange={(event) => {
                                                                setCoachAiModel(event.target.value);
                                                                setCoachAiPromptSaved(false);
                                                                setCoachAiPromptError('');
                                                            }}
                                                            className={styles.inputField}
                                                        >
                                                            {resolveAiModelOptions(coachAiModel).map((model) => (
                                                                <option key={model} value={model}>
                                                                    {getAiModelLabel(model)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <textarea
                                                        id="coachAiPrompt"
                                                        aria-label="AI training prompt"
                                                        value={coachAiPrompt}
                                                        onChange={(event) => {
                                                            setCoachAiPrompt(event.target.value);
                                                            setCoachAiPromptSaved(false);
                                                            setCoachAiPromptError('');
                                                        }}
                                                        placeholder="Add the coaching rules and instructions the AI should follow when generating trainings."
                                                        className={styles.textArea}
                                                    />

                                                    <textarea
                                                        id="coachAiParameters"
                                                        aria-label="AI training parameters"
                                                        value={coachAiParameters}
                                                        onChange={(event) => {
                                                            setCoachAiParameters(event.target.value);
                                                            setCoachAiPromptSaved(false);
                                                            setCoachAiPromptError('');
                                                        }}
                                                        placeholder="Add the default JSON parameters for this coach AI prompt."
                                                        className={styles.textArea}
                                                    />

                                                    {coachAiPromptSaved ? (
                                                        <p className={styles.variantSaved}>AI training prompt saved successfully.</p>
                                                    ) : null}

                                                    {coachAiPromptError ? (
                                                        <p className={styles.formError}>{coachAiPromptError}</p>
                                                    ) : null}

                                                    <button
                                                        type="button"
                                                        className={styles.submitButton}
                                                        disabled={coachAiPromptSaving}
                                                        onClick={handleCoachAiPromptSave}
                                                    >
                                                        {coachAiPromptSaving ? 'Saving...' : 'Save AI training prompt'}
                                                    </button>
                                                </div>
                                            ) : null}
                                        </section>
                                    ) : null}

                                    {experimentalAiEnabled ? (
                                        <AiUsageTable userId={userId} demoMode={demoMode} />
                                    ) : null}

                                    <section className={styles.notificationPanel}>
                                        <div className={styles.notificationPanelHeader}>
                                            <div>
                                                <h4>Notifications</h4>
                                                <p>Choose how you want to be notified when athletes complete a training inside your plans.</p>
                                            </div>
                                        </div>

                                        <label className={styles.preferenceRow}>
                                            <input
                                                type="checkbox"
                                                checked={emailNotificationsEnabled}
                                                onChange={(e) => {
                                                    setEmailNotificationsEnabled(e.target.checked);
                                                    setCoachProfileSaved(false);
                                                }}
                                            />
                                            <span>
                                                <strong>Email</strong>
                                                <small>Save your preference now so we can use it once coach email notifications go live.</small>
                                            </span>
                                        </label>
                                    </section>

                                    {coachProfileError ? (
                                        <p className={styles.formError}>{coachProfileError}</p>
                                    ) : null}

                                    <button type="submit" className={styles.submitButton} disabled={coachProfileSaving}>
                                        {coachProfileSaving ? 'Saving...' : 'Save changes'}
                                    </button>
                                </form>
                            </div>
                        </article>
                    </section>
                </>
            )}

            {showShareToast && (
                <Toast
                    message="Subscribed to shared training!"
                    onClose={() => setShowShareToast(false)}
                />
            )}
        </div>
    );
};

export default Profile;
